import {getActiveTutorialContext} from '../../configs/tutorial';
import {addNewPolicyToPolicyMarket, 
  addPendingMovementAction,
  addPendingRecruitmentAction,
  buyPolicy,
  moveUnit,
  recruitUnit,
  setHexToken,
  setSpecialBuilding,
  setStartingQuarter,
  toggleInitiativeInterception
} from '../../redux/actions';
import {getRandomPolicyForMarket} from '../policy/getRandomPolicyForMarket';
import {
  selectCurrentPlayer,
  selectPhase,
  selectPolicyMarket,
  selectRound,
  selectUnits,
  selectWarPhaseStage
} from '../../redux/reducers/sharedStateReducer/selectors';
import {getBuildingPrice} from '../economy';
import {getUnitPrice, getUnitTypeConfig} from '../../configs/units';
import {getSizeRecruitmentPrice} from '../recruitment/getSizeRecruitmentPrice';
import {enqueueTutorialDamageDice} from './tutorialDice';
import {executePhaseAutoActions} from '../gameState/turn/executePhaseAutoActions';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {keyToCoords} from '../coords/keyToCoords';

/**
 * Скриптованный бот режима обучения.
 *
 * Вместо decision-движка бот выполняет операции из chapter.botScript:
 * ключ "раунд|фаза|этап" -> массив операций, по одной за тик BotRunner
 * (паузы, звуки и анимации достаются бесплатно). Исчерпание операций ключа =
 * бот завершает фазу/этап штатным completeBotPhase (см. хук в executeBotStep).
 *
 * Операции:
 *   {op: 'placeStartingQuarter', hexKey}
 *   {op: 'build', token, hexKey}                — обычное строение
 *   {op: 'buildSpecial', token, hexKey}         — спецстроение/крепость
 *   {op: 'recruit', unitType, hexKey, unitId?}  — найм нового отряда
 *   {op: 'sizeRecruitment', unitHexKey, quarterHexKey}
 *   {op: 'move', fromHexKey, toHexKey, path?}   — path для анимации
 *   {op: 'interceptInitiative'}
 *   {op: 'buyPolicy', policyId}                 — покупка политики с рынка
 *   {op: 'autoSupply'}                          — штатная авто-раздача снабжения
 *   {op: 'dice', damage: [...]}                 — заправить кости перед боем
 *   {op: 'waitTutorialStepPassed', stepId}      — пауза бота, пока игрок не
 *                                                 прошел шаг сценария (например,
 *                                                 не закрыл обучающую модалку);
 *                                                 операция не потребляется, пока
 *                                                 условие не выполнено
 *   {op: 'waitTutorialModalClosed', stepId}     — пауза бота, пока игрок не
 *                                                 ЗАКРОЕТ модалку шага (сам шаг
 *                                                 может завершаться позже —
 *                                                 например, сменой фазы уже
 *                                                 после хода бота)
 *
 * Прогресс потребления операций — вне redux (по образцу счетчиков шагов в
 * executeBotStep): сбрасывается при старте главы.
 */

// Результат хука «бот ждет игрока»: executeBotStep не считает такой тик шагом
// (кап шагов фазы не должен принудительно завершить этап во время ожидания)
export const TUTORIAL_BOT_WAITING = 'tutorial_bot_waiting';

// Предел ожидания игрока в тиках BotRunner, а не в секундах: окно зависит от
// темпа главы (botActionIntervalMs, см. getTutorialBotActionIntervalMs) — около
// трех минут при общих 3 с и минута в главе 1 с ее секундой. Ожидание задумано
// как вежливость: бот не лезет со своим ходом, пока игрок читает модалку. Но
// если шаг, которого он ждет, стал недостижим — сценарий разошелся с тем, что
// игрок успел натворить, — ждать пришлось бы вечно, а игра стоит намертво: ход
// у бота, игроку сделать нечего. Ни один штатный предохранитель тут не
// сработает: тик ожидания сознательно обнуляет счетчик шагов фазы (см.
// executeBotStep), чтобы кап шагов не завершил этап досрочно. Поэтому предел
// свой — по его достижении бот перестает ждать и играет дальше: сбитая модалка
// лучше зависшей игры
export const TUTORIAL_BOT_MAX_WAIT_TICKS = 60;

let consumedOpsByKey = new Map();
// Сколько тиков подряд бот ждет на конкретной операции ожидания
let waitTicksByOp = new Map();

export const resetTutorialBotScript = () => {
  consumedOpsByKey = new Map();
  waitTicksByOp = new Map();
};

const getScriptKey = (state) => {
  const phase = selectPhase(state);
  const stage = phase === 'war' ? (selectWarPhaseStage(state) || '') : '';
  return `${selectRound(state)}|${phase}|${stage}`;
};

// Диспатчи скрипта помечаются флагом — гейт tutorialMiddleware пропускает их
// без сверки с шагом игрока
const createBotStoreWrapper = (store) => ({
  getState: () => store.getState(),
  dispatch: (action) => store.dispatch({...action, __tutorialBot: true}),
});

const executeOp = (store, botStore, op, currentPlayer, state) => {
  switch (op.op) {
    case 'placeStartingQuarter':
      botStore.dispatch(setStartingQuarter(currentPlayer.id, op.hexKey));
      return true;

    case 'build': {
      const {priceCoins, isFreeAction} = getBuildingPrice(op.token, currentPlayer, []);
      botStore.dispatch(setHexToken(op.hexKey, op.token, priceCoins, false, Boolean(isFreeAction)));
      return true;
    }

    case 'buildSpecial':
      botStore.dispatch(setSpecialBuilding(op.hexKey, op.token));
      return true;

    case 'recruit': {
      const {price} = getUnitPrice(op.unitType, currentPlayer);
      const unitId = op.unitId || `tutorial_bot_${Date.now()}`;
      botStore.dispatch(recruitUnit(op.hexKey, op.unitType, currentPlayer.factionId, price, unitId));
      botStore.dispatch(addPendingRecruitmentAction(unitId, 'UNIT_RECRUITMENT', op.hexKey, price, 0));
      return true;
    }

    case 'sizeRecruitment': {
      const unit = selectUnits(state).find((u) => u.hexKey === op.unitHexKey);
      if (!unit) {
        console.error(`[TutorialBot] sizeRecruitment: no unit at ${op.unitHexKey}`);
        return true;
      }
      const unitConfig = getUnitTypeConfig(unit.type);
      const {sizePrice} = getSizeRecruitmentPrice(unitConfig, currentPlayer);
      const sizeAdded = op.sizeAdded || 1;
      botStore.dispatch(addPendingRecruitmentAction(unit.id, 'SIZE_RECRUITMENT', op.quarterHexKey, sizePrice, sizeAdded));
      return true;
    }

    case 'move': {
      const unit = selectUnits(state).find((u) => u.hexKey === op.fromHexKey);
      if (!unit) {
        console.error(`[TutorialBot] move: no unit at ${op.fromHexKey}`);
        return true;
      }
      const dispatchMove = () => {
        botStore.dispatch(moveUnit(unit.id, unit.type, op.toHexKey));
        botStore.dispatch(addPendingMovementAction(unit.id, op.fromHexKey, op.toHexKey, false));
      };
      // Тот же прием, что у decision-движка (см. decideMovementStep):
      // фишка едет по пути через рендерер, диспатч — в колбэке анимации
      // animateUnitMovement ждет путь из координат {q, r}; в конфиге путь
      // записан ключами ячеек 'q,r'
      const animationPath = (op.path || []).map((hexKey) => {
        const [q, r] = keyToCoords(hexKey);
        return {q, r};
      });
      const renderer = REGISTRY_SINGLETON.getHexGridRenderer && REGISTRY_SINGLETON.getHexGridRenderer();
      if (renderer && renderer.animateUnitMovement && animationPath.length > 1 && !renderer.isAnimatingMovement) {
        renderer.animateUnitMovement(unit.id, animationPath, dispatchMove);
      } else {
        dispatchMove();
      }
      return true;
    }

    case 'interceptInitiative':
      botStore.dispatch(toggleInitiativeInterception());
      return true;

    case 'buyPolicy': {
      const policyMarket = selectPolicyMarket(state) || [];
      const policyIndex = policyMarket.findIndex((policy) => policy?.id === op.policyId);
      if (policyIndex === -1) {
        console.error(`[TutorialBot] buyPolicy: ${op.policyId} not on market`);
        return true;
      }
      botStore.dispatch(buyPolicy(policyIndex));
      // Рынок пополняется новой политикой взамен купленной — как в реальном
      // UI покупки и у decision-движка ботов (см. decidePolicyStep)
      botStore.dispatch(addNewPolicyToPolicyMarket(getRandomPolicyForMarket()));
      return true;
    }

    case 'autoSupply':
      // Штатная авто-раздача: сама распределит снабжение бота и завершит фазу
      executePhaseAutoActions(botStore, true, null);
      return true;

    case 'dice':
      enqueueTutorialDamageDice(op.damage || []);
      // Кости — мгновенная операция, сразу выполняем следующую
      return null;

    default:
      console.error(`[TutorialBot] Unknown op: ${op.op}`);
      return true;
  }
};

/**
 * Хук для executeBotStep: выполнить очередную операцию скрипта бота.
 * @returns {boolean|null} true — операция выполнена; false — операции ключа
 * исчерпаны (боту пора завершать фазу/этап); null — обучение неактивно,
 * работать должен обычный decision-движок
 */
export const maybeExecuteTutorialBotStep = (store) => {
  if (store.__isBotSandbox) {
    // Песочницы прогнозов не должны потреблять операции скрипта
    return null;
  }

  const state = store.getState();
  const context = getActiveTutorialContext(state);
  if (!context.chapter) {
    return null;
  }

  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer?.isBot) {
    return null;
  }

  const key = getScriptKey(state);
  const ops = context.chapter.botScript?.[key] || [];
  const botStore = createBotStoreWrapper(store);

  // Ждать дальше или сдаться и играть: см. TUTORIAL_BOT_MAX_WAIT_TICKS
  const keepWaiting = (waitKey, opDescription) => {
    const ticks = (waitTicksByOp.get(waitKey) || 0) + 1;
    waitTicksByOp.set(waitKey, ticks);
    if (ticks < TUTORIAL_BOT_MAX_WAIT_TICKS) {
      return true;
    }
    console.error(`[TutorialBot] ${opDescription}: ждали слишком долго, продолжаем без ожидания`);
    return false;
  };

  for (let guard = 0; guard < 20; guard++) {
    const consumedCount = consumedOpsByKey.get(key) || 0;
    if (consumedCount >= ops.length) {
      return false;
    }
    const op = ops[consumedCount];

    // Ожидание шага игрока: операция не потребляется, пока шаг не пройден —
    // бот «думает» (BotRunner продолжает тикать), а игрок читает модалку
    if (op.op === 'waitTutorialStepPassed') {
      const waitedStepIndex = context.chapter.steps.findIndex((step) => step.id === op.stepId);
      if (waitedStepIndex === -1) {
        console.error(`[TutorialBot] waitTutorialStepPassed: unknown step ${op.stepId}`);
      } else if (context.stepIndex <= waitedStepIndex
        && keepWaiting(`${key}|${consumedCount}`, `waitTutorialStepPassed ${op.stepId}`)
      ) {
        return TUTORIAL_BOT_WAITING;
      }
      consumedOpsByKey.set(key, consumedCount + 1);
      continue;
    }

    // Ожидание ЗАКРЫТИЯ модалки шага: продолжаем, когда шаг уже пройден ЛИБО
    // шаг активен, а его модалка была показана и закрыта. Для шагов, которые
    // завершаются позже самой модалки (например, сменой фазы после хода бота
    // — waitTutorialStepPassed там заблокировал бы игру намертво)
    if (op.op === 'waitTutorialModalClosed') {
      const waitedStepIndex = context.chapter.steps.findIndex((step) => step.id === op.stepId);
      if (waitedStepIndex === -1) {
        console.error(`[TutorialBot] waitTutorialModalClosed: unknown step ${op.stepId}`);
      } else {
        const tutorialState = state.private?.tutorial || {};
        const isModalClosed = context.stepIndex > waitedStepIndex
          || (context.stepIndex === waitedStepIndex
            && tutorialState.isStepModalShown
            && !tutorialState.activeModalId);
        if (!isModalClosed
          && keepWaiting(`${key}|${consumedCount}`, `waitTutorialModalClosed ${op.stepId}`)
        ) {
          return TUTORIAL_BOT_WAITING;
        }
      }
      consumedOpsByKey.set(key, consumedCount + 1);
      continue;
    }

    consumedOpsByKey.set(key, consumedCount + 1);
    const acted = executeOp(store, botStore, op, currentPlayer, store.getState());
    if (acted !== null) {
      return acted;
    }
  }
  return false;
};
